import {useLayoutEffect, useRef, useState} from 'react';

/**
 * Hook to detect if any MathJax content within a container would overflow
 * in a multi-column grid layout.
 *
 * This is used to conditionally switch MCQ options to single-column layout
 * when MathJax expressions are too wide for the multi-column grid.
 *
 * @param enabled - Whether to enable the overflow detection. When false, the hook
 *                  returns early without setting up observers.
 */
export const useMathJaxOverflow = (enabled = false) => {
  const ref = useRef<HTMLDivElement>(null);
  const [hasMathJaxOverflow, setHasMathJaxOverflow] = useState(false);

  useLayoutEffect(() => {
    if (!enabled) {
      return;
    }

    const container = ref.current;
    if (!container) {
      return;
    }

    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    const checkOverflow = () => {
      // Find all MathJax elements within the container
      const mathJaxElements = container.querySelectorAll('.MathJax, [class^="mjx"]');

      if (mathJaxElements.length === 0) {
        setHasMathJaxOverflow(false);
        return;
      }

      // Check if any MathJax element is wider than a reasonable column width
      // Use a threshold based on what would fit in a 2-column grid
      // The grid uses minmax(min(260px, 100%), 1fr), so we check against
      // roughly half the container width minus gap
      const containerWidth = container.clientWidth;
      const columnThreshold = (containerWidth - 16) / 2; // 16px is the gap

      let hasOverflow = false;
      mathJaxElements.forEach((el) => {
        const rect = el.getBoundingClientRect();
        // If any MathJax element is wider than what fits in a column, flag it
        if (rect.width > columnThreshold) {
          hasOverflow = true;
        }
      });

      setHasMathJaxOverflow(hasOverflow);
    };

    const debouncedCheck = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(checkOverflow, 100);
    };

    // Watch for MathJax rendering completion
    // MathJax adds elements with specific classes when done
    const observer = new MutationObserver((mutations) => {
      // Only check if MathJax-related elements were added
      const hasMathJaxMutation = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some(
          (node) =>
            node instanceof Element &&
            (node.classList?.contains('MathJax') || node.className?.toString().startsWith('mjx'))
        )
      );
      if (hasMathJaxMutation) {
        debouncedCheck();
      }
    });

    observer.observe(container, {
      childList: true,
      subtree: true
    });

    // Check on resize
    const resizeObserver = new ResizeObserver(debouncedCheck);
    resizeObserver.observe(container);

    // Initial check after MathJax has time to render
    checkOverflow();

    return () => {
      observer.disconnect();
      resizeObserver.disconnect();
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [enabled]);

  return {ref, hasMathJaxOverflow};
};
